import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '@/api/api';
import { Printer, Download, FileText, Eye, ArrowLeft } from 'lucide-react';
import { generateAdmissionReceipt } from '@/utils/admissionReceiptGenerator';
import { formatDate } from '../../utils/dateTimeUtils';

const AdmissionReport = () => {
  const { studentId } = useParams();
  const navigate = useNavigate();
  const [student, setStudent] = useState(null);
  const [course, setCourse] = useState(null);
  const [semester, setSemester] = useState(null);
  const [section, setSection] = useState(null);
  const [consultant, setConsultant] = useState(null);
  const [collegeInfo, setCollegeInfo] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadCollegeInfo = useCallback(async () => {
    try {
      const response = await api.get('/tenants/my-college/settings');
      if (response.data) {
        setCollegeInfo({
          ...response.data,
          college_name: response.data.name || response.data.college_name,
          logo_url: response.data.logo || response.data.logo_url
        });
      }
    } catch (error) {
      console.error('Error fetching college info:', error);
    }
  }, []);

  const fetchStudentData = useCallback(async () => {
    try {
      const response = await api.get(`/students/${studentId}`);
      const studentData = response.data;
      setStudent(studentData);
      
      console.log('📊 Student data loaded:', studentData);
      console.log('💰 Fee fields:', {
        admission_fee: studentData.admission_fee,
        admission_amount: studentData.admission_amount,
        seat_booking_amount: studentData.seat_booking_amount,
        booking_amount: studentData.booking_amount,
        total_course_fee: studentData.total_course_fee,
        balance_fee: studentData.balance_fee
      });

      // Fetch related data with error handling for each
      if (studentData.course_id) {
        try {
          const courseRes = await api.get(`/master-settings/courses/${studentData.course_id}`);
          setCourse(courseRes.data);
          console.log('✅ Course fetched:', courseRes.data.name);
        } catch (err) {
          console.error('❌ Error fetching course:', err);
        }
      }
      if (studentData.semester_id) {
        try {
          const semesterRes = await api.get(`/master-settings/semesters/${studentData.semester_id}`);
          setSemester(semesterRes.data);
          console.log('✅ Semester fetched:', semesterRes.data.name);
        } catch (err) {
          console.error('❌ Error fetching semester:', err);
        }
      }
      if (studentData.section_id) {
        try {
          const sectionRes = await api.get(`/master-settings/sections/${studentData.section_id}`);
          setSection(sectionRes.data);
          console.log('✅ Section fetched:', sectionRes.data.name);
        } catch (err) {
          console.error('❌ Error fetching section:', err);
        }
      }
      if (studentData.consultant_id) {
        try {
          const consultantRes = await api.get(`/consultants/${studentData.consultant_id}`);
          setConsultant(consultantRes.data);
          console.log('✅ Consultant fetched:', consultantRes.data.name);
        } catch (err) {
          console.error('❌ Error fetching consultant:', err);
        }
      }

      setLoading(false);
    } catch (error) {
      console.error('Error fetching student data:', error);
      setLoading(false);
    }
  }, [studentId]);

  useEffect(() => {
    fetchStudentData();
    loadCollegeInfo();
  }, [fetchStudentData, loadCollegeInfo]);

  const handlePrintReceipt = async () => {
    if (student) {
      console.log('=== ADMISSION REPORT RECEIPT GENERATION ===');
      console.log('student object:', student);
      console.log('  - full_name:', student.full_name);
      console.log('  - admission_number:', student.admission_number);
      console.log('  - course_id:', student.course_id);
      console.log('  - session:', student.session);
      console.log('course object:', course);
      console.log('  - course name:', course?.name);
      
      // Prepare receipt data in the correct format
      const receiptData = {
        receipt_no: student.admission_receipt_no || student.admission_number || `ADMRCT${Date.now()}`,
        student_name: student.full_name || 'Unknown Student',
        admission_no: student.admission_number || 'N/A',
        course_name: course?.name || 'N/A',
        session: student.session || 'N/A',
        total_course_fee: student.total_course_fee || 0,
        admission_date: student.admission_date || '',
        booking_amount: student.seat_booking_amount || student.booking_amount || 0,
        booking_receipt_no: student.booking_receipt_no || 'N/A',
        admission_fee: student.admission_fee || student.admission_amount || 0,
        admission_payment_mode: student.admission_payment_mode || 'N/A',
        admission_payment_modes: student.admission_payment_modes || [],
        discount_amount: student.discount_amount || student.total_discount || 0,
        discount_description: 'Applied Discounts',
        scholarship_amount: student.scholarship_amount || student.total_scholarship || 0,
        scholarship_description: 'Applied Scholarships',
        balance_fee: student.balance_fee || 0
      };
      
      console.log('=== FINAL receiptData for Admission Report ===');
      console.log(JSON.stringify(receiptData, null, 2));
      console.log('  - student_name:', receiptData.student_name);
      console.log('  - admission_no:', receiptData.admission_no);
      console.log('  - course_name:', receiptData.course_name);
      console.log('==========================================');
      
      await generateAdmissionReceipt(receiptData, collegeInfo);
    }
  };

  const handlePrintAdmissionForm = () => {
    window.print();
  };

  const handleViewDocument = (docData) => {
    // Handle both string URLs and document objects
    let docUrl = docData;
    
    // If it's an object, extract the data or URL
    if (typeof docData === 'object' && docData !== null) {
      // First check if there's base64 data
      if (docData.data && typeof docData.data === 'string') {
        // If data is already a data URL (data:image/png;base64,...)
        if (docData.data.startsWith('data:')) {
          docUrl = docData.data;
        } else {
          // If it's just base64 string, we need to add the proper prefix
          const mimeType = docData.mimetype || 'application/pdf';
          docUrl = `data:${mimeType};base64,${docData.data}`;
        }
      } else {
        // Fallback to url, filename, or path
        docUrl = docData.url || docData.filename || docData.path;
      }
    }
    
    // Check if it's a valid URL or data URL
    if (docUrl && typeof docUrl === 'string') {
      if (docUrl.startsWith('data:')) {
        // For base64 data URLs, open in new tab
        const newWindow = window.open();
        if (newWindow) {
          if (docUrl.startsWith('data:image')) {
            newWindow.document.write(`<html><body style="margin:0"><img src="${docUrl}" style="max-width:100%; height:auto"/></body></html>`);
          } else {
            newWindow.location.href = docUrl;
          }
        }
      } else if (docUrl.startsWith('http') || docUrl.startsWith('/')) {
        window.open(docUrl, '_blank');
      } else {
        alert('Document preview not available.');
      }
    } else {
      alert('Document preview not available.');
    }
  };

  const handleDownloadDocument = (docData, docName) => {
    let docUrl = docData;
    let filename = docName || 'document';
    
    // If it's an object, extract the data
    if (typeof docData === 'object' && docData !== null) {
      if (docData.filename) {
        filename = docData.filename;
      }
      
      if (docData.data && typeof docData.data === 'string') {
        // Handle base64 data
        if (docData.data.startsWith('data:')) {
          docUrl = docData.data;
        } else {
          const mimeType = docData.mimetype || 'application/pdf';
          docUrl = `data:${mimeType};base64,${docData.data}`;
        }
      } else {
        docUrl = docData.url || docData.filename || docData.path;
      }
    }
    
    // Download the document
    if (docUrl && typeof docUrl === 'string') {
      if (docUrl.startsWith('data:')) {
        // For base64 data, create a download link
        const link = document.createElement('a');
        link.href = docUrl;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } else if (docUrl.startsWith('http') || docUrl.startsWith('/')) {
        // For URLs, open in new tab or trigger download
        const link = document.createElement('a');
        link.href = docUrl;
        link.download = filename;
        link.target = '_blank';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } else {
        alert('Document download not available.');
      }
    } else {
      alert('Document download not available.');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-lg">Loading admission report...</div>
      </div>
    );
  }

  if (!student) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-lg text-red-600">Student not found</div>
      </div>
    );
  }

  // Calculate entry type
  const isLateralEntry = student.admission_number?.startsWith('LAT');
  const courseFee = isLateralEntry ? 300000 : 400000;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header with Action Buttons - Hide on print */}
      <div className="bg-white border-b border-gray-200 px-6 py-4 print:hidden">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate(-1)}
              className="flex items-center gap-2 text-gray-600 hover:text-gray-900"
            >
              <ArrowLeft className="w-5 h-5" />
              Back to Approve Admission
            </button>
            <h1 className="text-2xl font-bold text-gray-900">Admission Report</h1>
          </div>
          <div className="flex gap-3">
            <button
              onClick={handlePrintReceipt}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              <Printer className="w-4 h-4" />
              Print Receipt
            </button>
            <button
              onClick={handlePrintAdmissionForm}
              className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
            >
              <FileText className="w-4 h-4" />
              Print Admission Form
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="bg-white rounded-lg shadow-lg">
          {/* Header Section - For Print */}
          <div className="border-b border-gray-200 px-8 py-6 print:border-b-2 print:border-black">
            <div className="text-center">
              <h2 className="text-3xl font-bold text-gray-900">ADMISSION FORM</h2>
              <p className="text-gray-600 mt-2">Academic Year {student.session || 'N/A'}</p>
            </div>
            <div className="mt-6 flex justify-between items-center">
              <div>
                <p className="text-sm text-gray-600">Admission Number</p>
                <p className="text-xl font-bold text-blue-600">{student.admission_number}</p>
              </div>
              <div className="text-right">
                <p className="text-sm text-gray-600">Admission Date</p>
                <p className="text-xl font-semibold">
                  {formatDate(student.admission_date)}
                </p>
              </div>
            </div>
          </div>

          {/* Content Sections */}
          <div className="p-8 space-y-8">
            
            {/* Basic Information */}
            <div className="border-b border-gray-200 pb-6">
              <h3 className="text-xl font-bold text-gray-900 mb-4">Basic Information</h3>
              <div className="grid grid-cols-3 gap-6">
                <div>
                  <p className="text-sm text-gray-600">Full Name</p>
                  <p className="font-semibold text-gray-900">{student.full_name}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Email</p>
                  <p className="font-semibold text-gray-900">{student.email}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Phone</p>
                  <p className="font-semibold text-gray-900">{student.phone}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">WhatsApp</p>
                  <p className="font-semibold text-gray-900">{student.whatsapp_no || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Date of Birth</p>
                  <p className="font-semibold text-gray-900">
                    {student.date_of_birth ? formatDate(student.date_of_birth) : 'N/A'}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Gender</p>
                  <p className="font-semibold text-gray-900">{student.gender}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Nationality</p>
                  <p className="font-semibold text-gray-900">{student.nationality || 'Indian'}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Aadhar Number</p>
                  <p className="font-semibold text-gray-900">{student.aadhar_no || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Blood Group</p>
                  <p className="font-semibold text-gray-900">{student.blood_group || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Religion</p>
                  <p className="font-semibold text-gray-900">{student.religion || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Caste</p>
                  <p className="font-semibold text-gray-900">{student.caste || 'N/A'}</p>
                </div>
              </div>
            </div>

            {/* Current Address */}
            <div className="border-b border-gray-200 pb-6">
              <h3 className="text-xl font-bold text-gray-900 mb-4">Current Address</h3>
              <div className="grid grid-cols-3 gap-6">
                <div className="col-span-3">
                  <p className="text-sm text-gray-600">Address</p>
                  <p className="font-semibold text-gray-900">{student.address || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">City</p>
                  <p className="font-semibold text-gray-900">{student.city || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">State</p>
                  <p className="font-semibold text-gray-900">{student.state || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">District</p>
                  <p className="font-semibold text-gray-900">{student.district || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Pin Code</p>
                  <p className="font-semibold text-gray-900">{student.pin_code || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Landmark</p>
                  <p className="font-semibold text-gray-900">{student.landmark || 'N/A'}</p>
                </div>
              </div>
            </div>

            {/* Guardian Information */}
            <div className="border-b border-gray-200 pb-6">
              <h3 className="text-xl font-bold text-gray-900 mb-4">Guardian Information</h3>
              <div className="grid grid-cols-2 gap-8">
                {/* Father's Details */}
                <div className="space-y-4">
                  <h4 className="font-semibold text-gray-700">Father Details</h4>
                  <div className="space-y-3">
                    <div>
                      <p className="text-sm text-gray-600">Name</p>
                      <p className="font-semibold text-gray-900">{student.father_name || 'N/A'}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Phone</p>
                      <p className="font-semibold text-gray-900">{student.father_phone || 'N/A'}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Email</p>
                      <p className="font-semibold text-gray-900">{student.father_email || 'N/A'}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Occupation</p>
                      <p className="font-semibold text-gray-900">{student.father_occupation || 'N/A'}</p>
                    </div>
                  </div>
                </div>

                {/* Mother's Details */}
                <div className="space-y-4">
                  <h4 className="font-semibold text-gray-700">Mother Details</h4>
                  <div className="space-y-3">
                    <div>
                      <p className="text-sm text-gray-600">Name</p>
                      <p className="font-semibold text-gray-900">{student.mother_name || 'N/A'}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Phone</p>
                      <p className="font-semibold text-gray-900">{student.mother_phone || 'N/A'}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Email</p>
                      <p className="font-semibold text-gray-900">{student.mother_email || 'N/A'}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Occupation</p>
                      <p className="font-semibold text-gray-900">{student.mother_occupation || 'N/A'}</p>
                    </div>
                  </div>
                </div>

                {/* Guardian Details */}
                <div className="col-span-2 mt-4">
                  <h4 className="font-semibold text-gray-700 mb-3">Guardian Details</h4>
                  <div className="grid grid-cols-3 gap-6">
                    <div>
                      <p className="text-sm text-gray-600">Guardian Name</p>
                      <p className="font-semibold text-gray-900">{student.guardian_name || 'N/A'}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Guardian Phone</p>
                      <p className="font-semibold text-gray-900">{student.guardian_phone || 'N/A'}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Relation</p>
                      <p className="font-semibold text-gray-900">{student.guardian_relation || 'N/A'}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Academic Information */}
            <div className="border-b border-gray-200 pb-6">
              <h3 className="text-xl font-bold text-gray-900 mb-4">Academic Information</h3>
              <div className="grid grid-cols-2 gap-8">
                {/* Class 10 */}
                <div className="space-y-4">
                  <h4 className="font-semibold text-gray-700">Class 10th</h4>
                  <div className="space-y-3">
                    <div>
                      <p className="text-sm text-gray-600">Board</p>
                      <p className="font-semibold text-gray-900">{student.class_10_board || 'N/A'}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Year</p>
                      <p className="font-semibold text-gray-900">{student.class_10_year || 'N/A'}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Percentage</p>
                      <p className="font-semibold text-gray-900">{student.class_10_percentage ? `${student.class_10_percentage}%` : 'N/A'}</p>
                    </div>
                  </div>
                </div>

                {/* Class 12 */}
                <div className="space-y-4">
                  <h4 className="font-semibold text-gray-700">Class 12th</h4>
                  <div className="space-y-3">
                    <div>
                      <p className="text-sm text-gray-600">Board</p>
                      <p className="font-semibold text-gray-900">{student.class_12_board || 'N/A'}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Year</p>
                      <p className="font-semibold text-gray-900">{student.class_12_year || 'N/A'}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Percentage</p>
                      <p className="font-semibold text-gray-900">{student.class_12_percentage ? `${student.class_12_percentage}%` : 'N/A'}</p>
                    </div>
                  </div>
                </div>

                {/* Current Course */}
                <div className="col-span-2 mt-4">
                  <h4 className="font-semibold text-gray-700 mb-3">Current Course Details</h4>
                  <div className="grid grid-cols-4 gap-6">
                    <div>
                      <p className="text-sm text-gray-600">Course</p>
                      <p className="font-semibold text-gray-900">{course?.name || 'N/A'}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Session</p>
                      <p className="font-semibold text-gray-900">{student.session || 'N/A'}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Semester</p>
                      <p className="font-semibold text-gray-900">{semester?.name || 'N/A'}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Section</p>
                      <p className="font-semibold text-gray-900">{section?.name || 'N/A'}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Upload Documents */}
            <div className="border-b border-gray-200 pb-6">
              <h3 className="text-xl font-bold text-gray-900 mb-4">Uploaded Documents</h3>
              {student.documents && Object.keys(student.documents).length > 0 ? (
                <div className="grid grid-cols-2 gap-4">
                  {Object.entries(student.documents).map(([docName, docData]) => (
                    <div key={docName} className="border border-gray-200 rounded-lg p-4">
                      <p className="font-semibold text-gray-900 mb-2">{docName}</p>
                      <div className="flex gap-2 print:hidden">
                        <button
                          onClick={() => handleViewDocument(docData.url || docData)}
                          className="flex items-center gap-1 px-3 py-1 bg-blue-100 text-blue-700 rounded hover:bg-blue-200 text-sm"
                        >
                          <Eye className="w-4 h-4" />
                          View
                        </button>
                        <button
                          onClick={() => handleDownloadDocument(docData, docName)}
                          className="flex items-center gap-1 px-3 py-1 bg-green-100 text-green-700 rounded hover:bg-green-200 text-sm"
                        >
                          <Download className="w-4 h-4" />
                          Download
                        </button>
                      </div>
                      <p className="text-sm text-gray-600 mt-2 print:block hidden">Document attached</p>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500">No documents uploaded</p>
              )}
            </div>

            {/* Consultant & Fee Information */}
            <div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">Consultant & Fee Information</h3>
              <div className="grid grid-cols-3 gap-6">
                <div>
                  <p className="text-sm text-gray-600">Consultant</p>
                  <p className="font-semibold text-gray-900">{consultant?.name || 'Direct Admission'}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Course Fee</p>
                  <p className="font-semibold text-gray-900">₹{(student.total_course_fee || courseFee || 0).toLocaleString('en-IN')}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Net Course Fee</p>
                  <p className="font-semibold text-blue-700">₹{((student.total_course_fee || courseFee || 0) - (student.total_discount || student.discount_amount || 0) - (student.total_scholarship || student.scholarship_amount || 0) - (student.admission_amount || student.admission_fee || 0) - (student.seat_booking_amount || student.booking_amount || 0)).toLocaleString('en-IN')}</p>
                  <p className="text-xs text-gray-400">After all deductions</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Admission Fee</p>
                  <p className="font-semibold text-gray-900">₹{(student.admission_amount || student.admission_fee || 0).toLocaleString('en-IN')}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Booking Fee</p>
                  <p className="font-semibold text-gray-900">₹{(student.seat_booking_amount || student.booking_amount || 0).toLocaleString('en-IN')}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Discount</p>
                  <p className="font-semibold text-green-600">- ₹{(student.total_discount || student.discount_amount || 0).toLocaleString('en-IN')}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Scholarship</p>
                  <p className="font-semibold text-green-600">- ₹{(student.total_scholarship || student.scholarship_amount || 0).toLocaleString('en-IN')}</p>
                </div>
                <div className="col-span-3 pt-4 border-t-2 border-gray-300">
                  <p className="text-lg text-gray-600">Balance Fee Payable</p>
                  <p className="text-3xl font-bold text-blue-600">
                    ₹{(student.balance_fee || courseFee || 0).toLocaleString('en-IN')}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Footer - For Print */}
          <div className="border-t-2 border-gray-200 px-8 py-6 print:mt-8">
            <div className="flex justify-between items-end">
              <div>
                <p className="text-sm text-gray-600">Student Signature</p>
                <div className="border-b-2 border-gray-400 w-48 mt-8"></div>
              </div>
              <div className="text-center">
                <p className="text-sm text-gray-600">Parent/Guardian Signature</p>
                <div className="border-b-2 border-gray-400 w-48 mt-8"></div>
              </div>
              <div className="text-right">
                <p className="text-sm text-gray-600">Authorized Signature</p>
                <div className="border-b-2 border-gray-400 w-48 mt-8"></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdmissionReport;
